/**
 * Online/offline detection used by the GraphQL client and the status banner.
 * `aster-force-offline` lets testers simulate a dead network without turning Wi‑Fi off.
 */

export const FORCE_OFFLINE_KEY = "aster-force-offline";
export const FORCE_OFFLINE_COOKIE = "aster-force-offline";
export const NETWORK_SIM_EVENT = "aster-network-sim";

/**
 * True when the header toggle is forcing an offline session.
 *
 * @returns {boolean}
 */
export const isForceOffline = () => {
  if (typeof window === "undefined") return false;
  try {
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(FORCE_OFFLINE_KEY) === "1") return true;
    if (typeof localStorage !== "undefined" && localStorage.getItem(FORCE_OFFLINE_KEY) === "1") return true;
  } catch {
    return false;
  }
  return false;
};

/**
 * Turns simulated offline mode on or off and notifies listeners / the service worker.
 *
 * @param {boolean} offline Whether to simulate being offline.
 * @returns {void}
 */
export const setForceOffline = (offline) => {
  if (typeof window === "undefined") return;
  try {
    if (offline) {
      sessionStorage.setItem(FORCE_OFFLINE_KEY, "1");
      localStorage.setItem(FORCE_OFFLINE_KEY, "1");
    } else {
      sessionStorage.removeItem(FORCE_OFFLINE_KEY);
      localStorage.removeItem(FORCE_OFFLINE_KEY);
    }
  } catch {
    /* private mode */
  }

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  if (offline) {
    document.cookie = `${FORCE_OFFLINE_COOKIE}=1; path=/; SameSite=Lax${secure}`;
  } else {
    document.cookie = `${FORCE_OFFLINE_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`;
  }

  window.dispatchEvent(new CustomEvent(NETWORK_SIM_EVENT, { detail: !offline }));
};

/**
 * True when the browser reports a live network (best-effort; can be a lie on some OS).
 *
 * @returns {boolean}
 */
export const isOnline = () => {
  if (isForceOffline()) return false;
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
};

/**
 * True when an Axios/fetch failure looks like a connectivity problem, not 401/4xx.
 *
 * @param {Error} err Caught error.
 * @returns {boolean}
 */
export const isNetworkError = (err) => {
  if (!err) return false;
  if (isForceOffline()) return true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const code = err.code || err?.response?.status;
  if (code === "ERR_NETWORK" || code === "ECONNABORTED") return true;
  if (err.name === "AbortError") return true;
  const msg = String(err.message || "");
  if (msg === "Network Error") return true;
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(msg)) return true;
  if (!err.response && err.request) return true;
  return false;
};

/**
 * Subscribes to `online` / `offline` events and the test toggle.
 *
 * @param {(online: boolean) => void} listener Callback.
 * @returns {() => void} Unsubscribe.
 */
export const subscribeNetwork = (listener) => {
  if (typeof window === "undefined") return () => {};
  const notify = () => listener(isOnline());
  window.addEventListener("online", notify);
  window.addEventListener("offline", notify);
  window.addEventListener(NETWORK_SIM_EVENT, notify);
  return () => {
    window.removeEventListener("online", notify);
    window.removeEventListener("offline", notify);
    window.removeEventListener(NETWORK_SIM_EVENT, notify);
  };
};

/**
 * Confirms the GraphQL endpoint is actually reachable.
 * `navigator.onLine` can stay true on a dead wifi / captive portal, and a
 * cached `manifest.json` would look online even when it is not.
 *
 * @returns {Promise<boolean>}
 */
export const probeOnline = async () => {
  if (isForceOffline()) return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  const endpoint = process.env.REACT_APP_GQL_ENDPOINT;
  if (!endpoint || typeof fetch === "undefined") return isOnline();
  try {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 4000) : null;
    // Public single-field probe (auth middleware allows `__typename` without JWT).
    // Any HTTP response means the API is reachable; only transport failures mean offline.
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: "query aster_online_probe { __typename }" }),
      cache: "no-store",
      signal: ctrl ? ctrl.signal : undefined,
    });
    if (timer) clearTimeout(timer);
    return Boolean(res);
  } catch {
    return false;
  }
};
